import React, { useRef, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form, Formik, useFormikContext, FieldArray, getIn } from 'formik';
import * as yup from 'yup';
import i18n from 'i18n-js';
import * as FormikFields from 'formik-material-ui';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Autocomplete } from '@material-ui/lab';
import {
    TextField,
    InputAdornment,
    IconButton,
    FilledInput as NativeInput,
    InputLabel,
    FormControl,
    Typography,
    Box,
    alpha,
    FormGroup,
    FormControlLabel,
    Switch,
    Tabs,
    Tab,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from '@material-ui/core';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import RestaurantIcon from '@material-ui/icons/Restaurant';
import clsx from 'clsx';

import { AddIngredientsModal, Input } from 'components/shared';
import { default as restaurantStore } from 'store/restaurantDetailsStore';
import { DishCreateModel, IngredientModel } from 'api/models';
import { images } from 'assets';
import categoryStore from 'store/categoryStore';
import { DishData, SectionData, AdditionalDishSize } from 'store/menuDetailsStore/reducer';
import { trimLeftAndRight, truncateString } from 'helpers/helperFunctions';
import ImageContainer, {
    ImageContainerRefProps,
} from 'components/shared/components/ImageContainer';
import AllergensSelect from 'components/shared/components/AllergensSelect';
import ServiceTaxSelect from './ServiceTaxSelect';
import {
    OrderServices,
    TaxType,
    Positions,
    SpicinessLevels,
    Diets,
    DietaryLaws,
    MenuType,
} from 'constants/enums';
import { useStyles } from '../styles';
import { useAppSelector } from 'index';
import { regexHelper } from 'helpers/regexHelper';
import { getSpicinessLevels, getDiets, getDietaryLaws } from 'constants/maps';
import DishDietarySelect from 'components/shared/components/DishDietarySelect';
import TextEditor from 'components/shared/components/TextEditor';
import LabelSelect from 'components/shared/components/LabelSelect';
import SetFieldText from 'components/shared/components/SetFieldText';
import CodeScanner from 'components/shared/components/CodeScanner';
import menusStore from 'store/menusStore';
import { theme } from 'config/theme';

export interface DishManagementDialogProps {
    dish: DishData;
    disabled: boolean;
    sections: SectionData[];
    action: (model: DishCreateModel, id?: string, sectionId?: string) => void;
    isOpen: boolean;
    onClose: (event?: React.MouseEvent<HTMLElement>) => void;
    forCopy?: boolean;
    restaurantName?: string;
}

enum DishManagementTabs {
    MAIN,
    ADVANCED,
    PRICE_MANAGEMENT,
}

export default function DishManagementDialog(props: DishManagementDialogProps) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const categoryDetails = useAppSelector((state) => state.category.categoryDetails);
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const user = useAppSelector((state) => state.user);
    const taxes = useAppSelector((state) => state.restaurantDetails.taxes);
    const lang = useAppSelector((state) => state.global.language);
    const menu = useAppSelector((state) => state.menuDetails.menu);
    const dishes = useRef<string[]>([]);
    const childRef = useRef<ImageContainerRefProps>(null);
    const scanTries = useRef<null | ReturnType<typeof setTimeout>>(null);
    const [showAddIngredientsModal, setShowAddIngredientsModal] = useState(false);
    const [isDeniedTaxSetup, setIsDeniedTaxSetup] = useState(true);
    const [showAdditionalForm, setShowAdditionalForm] = useState(false);
    const [advancedSettingsHasError, setAdvancedSettingsHasError] = useState(false);
    const [showEditor, setShowEditor] = useState(false);
    const [textToEdit, setTextToEdit] = useState('');
    const [showScanner, setShowScanner] = useState(false);
    const [valueName, setValueName] = useState('');
    const [tabActive, setTab] = useState<DishManagementTabs>(DishManagementTabs.MAIN);
    const title = useRef<HTMLDivElement>(null);
    const currentRestaurant = user.restaurants?.find((x) => x.id === user.currentRestaurantId);
    const currentPosition = currentRestaurant?.currentPosition;
    const isCatalogManager =
        (menu.type === MenuType.Catalog &&
            currentPosition === Positions.MANAGER &&
            menu.catalogData?.allowToEditCatalog) ??
        false;
    const isCatalog = menu.type === MenuType.Catalog;
    const dishList = useAppSelector((state) => state.menus.dishList);

    function handleChangeTab(event: React.ChangeEvent<{}>, view: number) {
        console.log(view);
        if (view !== tabActive) {
            setTab(view);
        }
    }

    function saveTextFromEditor(text: string) {
        setTextToEdit(text);
    }

    function preventProp(event: React.MouseEvent<HTMLElement>) {
        event.stopPropagation();
    }

    function onCancel() {
        setShowAdditionalForm(false);
        props.onClose();
    }

    function handleShowAdvancedSettingsError(hasError: boolean) {
        setAdvancedSettingsHasError(hasError);
    }

    function handleScannerClose() {
        setShowScanner(false);
        if (scanTries.current) {
            clearTimeout(scanTries.current);
        }
    }

    function handleScannerOpen() {
        setShowScanner(true);
        scanTries.current = setTimeout(() => handleScannerClose(), 10000) as ReturnType<
            typeof setTimeout
        >;
    }

    function preparePricelevels(additionalSize?: AdditionalDishSize) {
        let priceLevels: {
            price: string;
            priceLevelId: string;
        }[] = [];
        if (additionalSize) {
            if (!!additionalSize.priceLevels?.length) {
                additionalSize.priceLevels.forEach((pl) =>
                    priceLevels.push({ price: pl.price.toFixed(2), priceLevelId: pl.priceLevelId })
                );
            }
        } else {
            if (!!props.dish?.priceLevels?.length) {
                props.dish?.priceLevels.forEach((pl) =>
                    priceLevels.push({ price: pl.price.toFixed(2), priceLevelId: pl.priceLevelId })
                );
            }
        }

        restaurant.pricingLevels.forEach((pl) => {
            if (priceLevels.every((pr) => pr.priceLevelId !== pl.id)) {
                priceLevels.push({ priceLevelId: pl.id, price: props.dish.price.toFixed(2) });
            }
        });

        return priceLevels;
    }

    useEffect(() => {
        setIsDeniedTaxSetup(
            user.restaurants.find((x) => x.id === user.currentRestaurantId)?.currentPosition !==
                Positions.MANAGER
        );
        if (!restaurant.id) {
            dispatch(restaurantStore.actionCreators.getRestaurantInformation());
        }
        if (!categoryDetails) {
            dispatch(categoryStore.actionCreators.getCategoryDetails());
        }
    }, [categoryDetails]);

    useEffect(() => {
        if (props.isOpen && !!!taxes.id) {
            dispatch(restaurantStore.actionCreators.getTaxes());
        }

        if (props.isOpen) {
            dispatch(menusStore.actionCreators.getDishList());
        }
    }, [props.isOpen]);

    useEffect(() => {
        if (props.dish.catalogData?.catalogId) {
            const currentCatalogDishes = menu.sections
                .flatMap((s) => s.dishes)
                .filter((dish) => dish.id !== props.dish.id);
            const additionalCatalogDishes = dishList.filter(
                (d) =>
                    d.id !== props.dish.id &&
                    d.catalogData?.catalogId === props.dish.catalogData?.catalogId
            );
            dishes.current = Array.from(
                new Set(
                    [...currentCatalogDishes, ...additionalCatalogDishes].map((dish) =>
                        dish.name.toUpperCase()
                    )
                )
            );
        } else {
            // check all non-catalog dishes
            dishes.current = dishList
                .filter((d) => d.id !== props.dish.id && !d.catalogData?.catalogId)
                .map((dish) => dish.name.toUpperCase());
        }
    }, [dishList]);

    const initialFormValues = {
        name: props.forCopy ? 'copy of ' + props.dish.name : props.dish.name,
        shortName: props.dish.shortName,
        description: props.dish.description,
        shortDescription: props.dish.shortDescription,
        price: props.dish.price!.toFixed(2),
        photo: props.dish.photo,
        section: props.dish.sectionId || '',
        addToGallery: props.dish.addToGallery,
        category: props.dish.categoryId || '',
        photoSelected: props.dish.photo.length > 0,
        allergens: props.dish.allergens,
        ingredients: props.dish.ingredients,
        primeCost: props.dish.primeCost?.toFixed(2) ?? 0,
        dineInTax: props.dish.dineInTax ?? TaxType.None,
        homeDeliveryTax: props.dish.homeDeliveryTax ?? TaxType.None,
        takeAwayTax: props.dish.takeAwayTax ?? TaxType.None,
        barcode: props.dish.barcode,
        stockQuantity: props.dish.stockQuantity,
        minimumStock: props.dish.minimumStock,
        notFood: props.dish.notFood,
        color: props.dish.color,
        spicinessLevel: props.dish.spicinessLevel || SpicinessLevels.None,
        diet: props.dish.diet || Diets.None,
        dietaryLaw: props.dish.dietaryLaw || DietaryLaws.None,
        labels: props.dish.labels?.map((label) => label.id) || [],
        size: props.dish.size,
        additionalSizes: props.dish.additionalSizes.map((x) => ({
            ...x,
            price: x.price.toFixed(2),
            priceLevels: preparePricelevels(x),
        })),
        priceLevels: preparePricelevels(),
        isCustomPrice: !!props.dish.isCustomPrice,
        externalId: props.dish.externalId?.toString(),
        supplierName: props.dish.supplierName,
    };

    function validateTaxes(name: string, values: any) {
        switch (name) {
            case 'dineInTax':
                if (!restaurant.dineIn.isActive) {
                    return true;
                }
                break;
            case 'homeDeliveryTax':
                if (!restaurant.homeDelivery.isActive) {
                    return true;
                }
                break;
            case 'takeAwayTax':
                if (!restaurant.takeAway.isActive) {
                    return true;
                }
                break;
            default:
                return true;
        }
        if (restaurant.dineIn.isActive && values.dineInTax !== TaxType.None) {
            return true;
        }
        if (restaurant.takeAway.isActive && values.takeAwayTax !== TaxType.None) {
            return true;
        }
        if (restaurant.homeDelivery.isActive && values.homeDeliveryTax !== TaxType.None) {
            return true;
        }
        return false;
    }

    function isFieldDisabled(name: string, additionalOption: boolean = false) {
        if (name === 'save') {
            if (isCatalog && isCatalogManager && !!props.restaurantName) {
                return false;
            }
            return props.disabled;
        }

        if (name === 'stockQuantity') {
            return currentPosition !== Positions.MANAGER;
        }
        if (['externalId', 'supplierName', 'category'].includes(name)) {
            return false;
        }
        if (name === 'price') {
            if (props.restaurantName && isCatalogManager) {
                return false;
            }
            return props.disabled || additionalOption || menu.type === MenuType.MultiCourse;
        }
        return props.disabled || (isCatalog && !isCatalogManager);
    }

    const validationSchema = yup.object().shape({
        name: yup
            .string()
            .required(
                i18n.t('form.errors.required', {
                    name: 'Name',
                })
            )
            .test(
                'existsCheck',
                i18n.t('form.errors.alreadyTaken'),
                (value: any) => !dishes.current.includes(value?.trim().toUpperCase())
            ),
        shortName: yup.string().required(
            i18n.t('form.errors.required', {
                name: 'Short name',
            })
        ),
        price: yup
            .number()
            .typeError(i18n.t('form.errors.onlyDigits'))
            .min(0, i18n.t('form.errors.greaterZero'))
            .test('is-decimal', i18n.t('form.errors.twoDigitsAfterComma'), (val?: number) => {
                if (val) {
                    return regexHelper.twoDigisAfterComma.test(val.toString());
                }
                return true;
            }),
        primeCost: yup
            .number()
            .test('is-decimal', i18n.t('form.errors.twoDigitsAfterComma'), (val?: number) => {
                if (val) {
                    return regexHelper.twoDigisAfterComma.test(val.toString());
                }
                return true;
            })
            .typeError(i18n.t('form.errors.onlyDigits'))
            .min(0, i18n.t('form.errors.greaterZero')),
        category: (() => {
            return props.restaurantName
                ? yup.string().notRequired()
                : yup.string().required(
                      i18n.t('form.errors.required', {
                          name: 'Work Area',
                      })
                  );
        })(),
        section: (() => {
            return props.restaurantName
                ? yup.string().notRequired()
                : yup.string().required(
                      i18n.t('form.errors.required', {
                          name: 'Section',
                      })
                  );
        })(),
        dineInTax: yup
            .string()
            .test(
                'selectsCheck',
                i18n.t('form.errors.atLeasOneTaxSelected'),
                (value: any, context: any) => validateTaxes('dineInTax', context.parent)
            ),
        homeDeliveryTax: yup
            .string()
            .test(
                'selectsCheck',
                i18n.t('form.errors.atLeasOneTaxSelected'),
                (value: any, context: any) => validateTaxes('homeDeliveryTax', context.parent)
            ),
        takeAwayTax: yup
            .string()
            .test(
                'selectsCheck',
                i18n.t('form.errors.atLeasOneTaxSelected'),
                (value: any, context: any) => validateTaxes('takeAwayTax', context.parent)
            ),
        barcode: yup
            .string()
            .matches(
                regexHelper.lettersAndNumbersOnly,
                i18n.t('form.errors.lettersAndNumbersOnly')
            ),
        stockQuantity: yup
            .number()
            .integer(i18n.t('form.errors.onlyWholeNumbers'))
            .nullable()
            .typeError(i18n.t('form.errors.onlyDigits'))
            .min(0, i18n.t('form.errors.greaterZero')),
        minimumStock: yup
            .number()
            .integer(i18n.t('form.errors.onlyWholeNumbers'))
            .typeError(i18n.t('form.errors.onlyDigits'))
            .min(0, i18n.t('form.errors.greaterZero')),
        size: yup.string().when('additionalSizes', {
            is: (value: []) => !!value.length,
            then: yup.string().required(
                i18n.t('form.errors.required', {
                    name: 'Size',
                })
            ),
            otherwise: yup.string().notRequired(),
        }),
        additionalSizes: yup.array().of(
            yup.object().shape({
                size: yup.string().required(
                    i18n.t('form.errors.required', {
                        name: i18n.t('common.size'),
                    })
                ),
                price: yup
                    .number()
                    .typeError(i18n.t('form.errors.onlyDigits'))
                    .min(0, i18n.t('form.errors.greaterZero'))
                    .test(
                        'is-decimal',
                        i18n.t('form.errors.twoDigitsAfterComma'),
                        (val?: number) => {
                            if (val) {
                                return regexHelper.twoDigisAfterComma.test(val.toString());
                            }
                            return true;
                        }
                    ),
                priceLevels: yup.array().of(
                    yup.object().shape({
                        price: yup
                            .number()
                            .typeError(i18n.t('form.errors.onlyDigits'))
                            .min(0, i18n.t('form.errors.greaterZero'))
                            .test(
                                'is-decimal',
                                i18n.t('form.errors.twoDigitsAfterComma'),
                                (val?: number) => {
                                    if (val) {
                                        return regexHelper.twoDigisAfterComma.test(val.toString());
                                    }
                                    return true;
                                }
                            ),
                    })
                ),
            })
        ),
        priceLevels: yup.array().of(
            yup.object().shape({
                price: yup
                    .number()
                    .typeError(i18n.t('form.errors.onlyDigits'))
                    .min(0, i18n.t('form.errors.greaterZero'))
                    .test(
                        'is-decimal',
                        i18n.t('form.errors.twoDigitsAfterComma'),
                        (val?: number) => {
                            if (val) {
                                return regexHelper.twoDigisAfterComma.test(val.toString());
                            }
                            return true;
                        }
                    ),
            })
        ),
        externalId: yup
            .number()
            .nullable()
            .typeError(i18n.t('form.errors.onlyDigits'))
            .min(0, i18n.t('form.errors.greaterZero'))
            .max(
                Number.MAX_SAFE_INTEGER,
                i18n.t('form.errors.lessThan', { value: Number.MAX_SAFE_INTEGER })
            ),
    });

    function formDisabled(values: any) {
        let result = !props.forCopy;
        if (!props.forCopy && values) {
            result = JSON.stringify(initialFormValues) === JSON.stringify(values);
        }
        return result;
    }

    function addToAllergens(
        ingredients: IngredientModel[],
        values: any,
        setFieldValue: (key: string, value: any) => void
    ) {
        const newAllergens: string[] = [...values.allergens];
        ingredients.forEach((eachIngredient) => {
            eachIngredient.allergens.forEach((allergen) => {
                if (!newAllergens.includes(allergen)) {
                    newAllergens.push(allergen);
                }
            });
        });
        setFieldValue('allergens', newAllergens);
    }

    return (
        <>
            <Dialog
                maxWidth="lg"
                open={props.isOpen}
                onClick={preventProp}
                aria-labelledby="form-dialog-title"
                className={classes.dialog}
                PaperProps={{
                    style: {
                        position: 'absolute',
                        top: 30,
                        margin: 0,
                    },
                }}
            >
                {props.restaurantName && (
                    <Box
                        bgcolor={alpha(theme.palette.primary.main, 0.4)}
                        color={theme.palette.primary.dark}
                        padding="5px 0 5px 17px"
                        fontWeight={600}
                    >
                        <RestaurantIcon style={{ marginRight: 5, height: 15 }} />
                        {props.restaurantName}
                    </Box>
                )}
                <DialogTitle
                    id="form-dialog-title"
                    className={clsx(classes.dialogTitle, {
                        [classes.dialogTitleExtended]: showAdditionalForm,
                    })}
                    ref={title}
                >
                    {props.disabled ? props.dish.name : i18n.t('common.editItem')}{' '}
                    {/* <div onClick={() => setShowAdditionalForm(true)}>
                        {advancedSettingsHasError && !showAdditionalForm ? (
                            <div className={classes.hasError}>
                                <h5>
                                    <img src={images.icons.settingsError} alt="settings" />
                                    {i18n.t('common.advancedSettings')}
                                </h5>
                                <span>{i18n.t('form.errors.hasIncorrectData')}</span>
                            </div>
                        ) : (
                            <h5>
                                <img src={images.icons.settings} alt="settings" />
                                {i18n.t('common.advancedSettings')}
                            </h5>
                        )}
                    </div> */}
                </DialogTitle>
                <Tabs
                    value={tabActive}
                    onChange={handleChangeTab}
                    aria-label="wrapped label tabs example"
                    indicatorColor="primary"
                    textColor="primary"
                    style={{
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                        paddingLeft: 25,
                    }}
                >
                    <Tab value={DishManagementTabs.MAIN} label={i18n.t('navigationTabs.main')} />
                    <Tab
                        value={DishManagementTabs.ADVANCED}
                        label={i18n.t('navigationTabs.advanced')}
                    />
                    {restaurant.pricingLevels.length > 0 && (
                        <Tab
                            value={DishManagementTabs.PRICE_MANAGEMENT}
                            label={i18n.t('navigationTabs.priceManagement')}
                        />
                    )}
                </Tabs>
                <DialogContent>
                    <Formik
                        enableReinitialize={true}
                        initialValues={initialFormValues}
                        validationSchema={validationSchema}
                        onSubmit={async (values, { resetForm }) => {
                            const parsed = Number(values.externalId);
                            const externalId =
                                Number.isInteger(parsed) && parsed >= 0 ? parsed : null;

                            childRef?.current
                                ?.uploadImage()
                                .then((photo) => {
                                    values.photo = photo;
                                    let price = values.price.replaceAll(' ', '');
                                    const model: DishCreateModel = {
                                        name: trimLeftAndRight(values.name),
                                        shortName: values.shortName,
                                        description: trimLeftAndRight(values.description),
                                        shortDescription: trimLeftAndRight(values.shortDescription),
                                        price: +price,
                                        photo: values.photo,
                                        sectionId: values.section,
                                        addToGallery: values.addToGallery,
                                        categoryId: values.category,
                                        allergens: values.allergens,
                                        ingredients: values.ingredients,
                                        primeCost: +values.primeCost,
                                        dineInTax: values.dineInTax,
                                        homeDeliveryTax: values.homeDeliveryTax,
                                        takeAwayTax: values.takeAwayTax,
                                        barcode: values.barcode,
                                        stockQuantity: values.stockQuantity,
                                        minimumStock: values.minimumStock,
                                        notFood: values.notFood,
                                        color: props.dish.color,
                                        spicinessLevel: values.spicinessLevel,
                                        diet: values.diet,
                                        dietaryLaw: values.dietaryLaw,
                                        labels: values.labels,
                                        size: values.size,
                                        priceLevels: values.priceLevels.map((pl, i) => {
                                            return {
                                                priceLevelId: pl.priceLevelId,
                                                price: !!!props.dish?.id
                                                    ? +values.price
                                                    : +pl.price,
                                            };
                                        }),
                                        additionalSizes: values.additionalSizes.map((x, i) => {
                                            const initialSize =
                                                initialFormValues.additionalSizes[i];
                                            return {
                                                ...x,
                                                price: +x.price,
                                                priceLevels: x.priceLevels.map((pl) => ({
                                                    priceLevelId: pl.priceLevelId,
                                                    price: !!initialSize ? +pl.price : +x.price,
                                                })),
                                            };
                                        }),
                                        isCustomPrice: values.isCustomPrice,
                                        externalId: externalId,
                                        supplierName: values.supplierName,
                                    };
                                    props.action(model, props.dish.id);
                                })
                                .then(() => {
                                    resetForm();
                                    props.onClose();
                                })
                                .catch((error) => alert(error));
                        }}
                    >
                        {({
                            values,
                            touched,
                            setFieldValue,
                            errors,
                            submitForm,
                            setFieldError,
                        }) => (
                            <Form className={classes.root} style={{ textAlign: 'start' }}>
                                <>
                                    <Box
                                        display={
                                            tabActive === DishManagementTabs.MAIN ? 'block' : 'none'
                                        }
                                    >
                                        <Box
                                            display="flex"
                                            flexDirection="row"
                                            width="100%"
                                            justifyContent="space-between"
                                        >
                                            <Box display="flex" flexDirection="column" width="60%">
                                                <Box className={classes.photoContainer}>
                                                    <ImageContainer
                                                        disabled={isFieldDisabled('photo')}
                                                        ref={childRef}
                                                        photo={values.photo}
                                                        onChange={(selected, fileUrl) => {
                                                            setFieldValue(
                                                                'photoSelected',
                                                                selected
                                                            );
                                                            setFieldValue('photo', fileUrl);
                                                        }}
                                                        styles={{ marginBottom: 0, height: 223 }}
                                                    />
                                                </Box>
                                                <Box
                                                    visibility={
                                                        isFieldDisabled('photo')
                                                            ? 'hidden'
                                                            : 'visible'
                                                    }
                                                    textAlign="start"
                                                    padding="8px 0 0 0"
                                                >
                                                    <Input
                                                        disabled={!values.photoSelected}
                                                        component={FormikFields.CheckboxWithLabel}
                                                        variant="filled"
                                                        checked={values.addToGallery}
                                                        name="addToGallery"
                                                        type="checkbox"
                                                        Label={{
                                                            label: i18n.t('form.addPhotoToGallery'),
                                                        }}
                                                        placeholder={i18n.t(
                                                            'form.addPhotoToGallery'
                                                        )}
                                                        color="primary"
                                                    />
                                                </Box>
                                            </Box>

                                            <Box display="flex" flexDirection="column" width="37%">
                                                <div>
                                                    <ServiceTaxSelect
                                                        service={OrderServices.DineIn}
                                                        selectedTax={values.dineInTax}
                                                        taxes={taxes}
                                                        onSelect={(tax: TaxType) =>
                                                            setFieldValue('dineInTax', tax)
                                                        }
                                                        disabled={
                                                            !!props.restaurantName ||
                                                            (props.disabled ||
                                                            isDeniedTaxSetup ||
                                                            isCatalog
                                                                ? !isCatalogManager
                                                                : !restaurant.dineIn.isActive)
                                                        }
                                                        error={errors.dineInTax}
                                                    />
                                                    <ServiceTaxSelect
                                                        service={OrderServices.TakeAway}
                                                        selectedTax={values.takeAwayTax}
                                                        taxes={taxes}
                                                        onSelect={(tax: TaxType) =>
                                                            setFieldValue('takeAwayTax', tax)
                                                        }
                                                        disabled={
                                                            !!props.restaurantName ||
                                                            (props.disabled ||
                                                            isDeniedTaxSetup ||
                                                            isCatalog
                                                                ? !isCatalogManager
                                                                : !restaurant.takeAway.isActive)
                                                        }
                                                        error={errors.takeAwayTax}
                                                    />
                                                    <ServiceTaxSelect
                                                        service={OrderServices.HomeDelivery}
                                                        selectedTax={values.homeDeliveryTax}
                                                        taxes={taxes}
                                                        onSelect={(tax: TaxType) =>
                                                            setFieldValue('homeDeliveryTax', tax)
                                                        }
                                                        styles={{ marginBottom: 0 }}
                                                        disabled={
                                                            !!props.restaurantName ||
                                                            (props.disabled ||
                                                            isDeniedTaxSetup ||
                                                            isCatalog
                                                                ? !isCatalogManager
                                                                : !restaurant.homeDelivery.isActive)
                                                        }
                                                        error={errors.homeDeliveryTax}
                                                    />
                                                </div>
                                                <Box display="flex" marginTop={1}>
                                                    <Input
                                                        component={FormikFields.CheckboxWithLabel}
                                                        disabled={isFieldDisabled('notFood')}
                                                        variant="filled"
                                                        name="notFood"
                                                        type="checkbox"
                                                        Label={{ label: i18n.t('form.notFood') }}
                                                        placeholder={i18n.t('form.notFood')}
                                                        inputProps={{
                                                            'aria-label': 'secondary checkbox',
                                                        }}
                                                        color="primary"
                                                    />
                                                    <Input
                                                        component={FormikFields.CheckboxWithLabel}
                                                        disabled={isFieldDisabled('isCustomPrice')}
                                                        variant="filled"
                                                        name="isCustomPrice"
                                                        type="checkbox"
                                                        Label={{
                                                            label: i18n.t('common.customPrice'),
                                                        }}
                                                        placeholder={i18n.t('common.customPrice')}
                                                        inputProps={{
                                                            'aria-label': 'secondary checkbox',
                                                        }}
                                                        color="primary"
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Box
                                            display="flex"
                                            flexDirection="row"
                                            width="100%"
                                            justifyContent="space-between"
                                            marginTop={'7px'}
                                        >
                                            <Box display="flex" flexDirection="column" width="60%">
                                                <Input
                                                    disabled={isFieldDisabled('name')}
                                                    variant="filled"
                                                    fullWidth={true}
                                                    name="name"
                                                    type="text"
                                                    error={errors.name}
                                                    label={i18n.t('form.itemName')}
                                                    placeholder={i18n.t('form.itemNamePlaceholder')}
                                                />
                                                <Input
                                                    disabled={isFieldDisabled('shortName')}
                                                    variant="filled"
                                                    name="shortName"
                                                    error={errors.shortName}
                                                    type="text"
                                                    label={i18n.t('form.shortName')}
                                                    placeholder={i18n.t(
                                                        'form.shortNamePlaceholder'
                                                    )}
                                                />
                                            </Box>
                                            <Box display="flex" flexDirection="column" width="37%">
                                                <Autocomplete
                                                    id="section"
                                                    disabled={true}
                                                    options={props.sections.map((x) => x.id)}
                                                    getOptionLabel={(option) =>
                                                        props.sections.find((x) => x.id === option)
                                                            ?.name ?? ''
                                                    }
                                                    value={values.section}
                                                    getOptionSelected={(option, value) =>
                                                        option === value
                                                    }
                                                    onChange={(e, value) => {
                                                        setFieldValue('section', value || '');
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            name="section"
                                                            variant="filled"
                                                            fullWidth
                                                            label={
                                                                Boolean(
                                                                    touched.section &&
                                                                        errors.section
                                                                )
                                                                    ? i18n.t(
                                                                          'form.errors.required',
                                                                          {
                                                                              name: 'Section',
                                                                          }
                                                                      )
                                                                    : i18n.t('common.section')
                                                            }
                                                            error={Boolean(
                                                                touched.section && errors.section
                                                            )}
                                                        />
                                                    )}
                                                />
                                                <Autocomplete
                                                    id="category"
                                                    disabled={isFieldDisabled('category')}
                                                    options={
                                                        categoryDetails?.categories?.map(
                                                            (x) => x.id
                                                        ) || []
                                                    }
                                                    getOptionLabel={(option) =>
                                                        categoryDetails?.categories?.find(
                                                            (x) => x.id === option
                                                        )?.name ?? ''
                                                    }
                                                    value={values.category}
                                                    getOptionSelected={(option, value) =>
                                                        option === value
                                                    }
                                                    onChange={(e, value) =>
                                                        setFieldValue('category', value || '')
                                                    }
                                                    renderInput={(params) => (
                                                        <>
                                                            <TextField
                                                                {...params}
                                                                name="categories"
                                                                label={
                                                                    Boolean(
                                                                        touched.category &&
                                                                            errors.category
                                                                    )
                                                                        ? i18n.t(
                                                                              'form.errors.required',
                                                                              {
                                                                                  name: 'Work Area',
                                                                              }
                                                                          )
                                                                        : i18n.t(
                                                                              'restaurant.workArea'
                                                                          )
                                                                }
                                                                variant="filled"
                                                                fullWidth
                                                                error={Boolean(
                                                                    touched.category &&
                                                                        errors.category
                                                                )}
                                                            />
                                                        </>
                                                    )}
                                                />
                                            </Box>
                                        </Box>
                                        <Input
                                            fullWidth
                                            disabled={isFieldDisabled('shortDescription')}
                                            variant="filled"
                                            name="shortDescription"
                                            error={errors.shortDescription}
                                            type="text"
                                            label={i18n.t('form.shortDescription')}
                                            placeholder={i18n.t('form.shortDescriptionPlaceholder')}
                                        />
                                        <FormControl
                                            fullWidth
                                            variant="filled"
                                            style={{
                                                textAlign: 'start',
                                            }}
                                            disabled={isFieldDisabled('description')}
                                        >
                                            <InputLabel htmlFor="filled-adornment-description">
                                                {i18n.t('form.description')}
                                            </InputLabel>
                                            <NativeInput
                                                id="filled-adornment-description"
                                                type="text"
                                                onClick={() => {
                                                    setTextToEdit(values.description);
                                                    setValueName('description');
                                                    setShowEditor(true);
                                                }}
                                                readOnly
                                                value={truncateString(
                                                    values.description.replace(
                                                        regexHelper.removeAllTags,
                                                        ''
                                                    ),
                                                    55
                                                )}
                                                endAdornment={
                                                    (!isCatalog && !props.disabled) ||
                                                    (isCatalog &&
                                                        !props.restaurantName &&
                                                        isCatalogManager) ? (
                                                        <InputAdornment position="end">
                                                            <IconButton aria-label="pen">
                                                                <img
                                                                    src={images.icons.penBlack}
                                                                    alt="pen-black"
                                                                    className={
                                                                        classes.resetSearchIcon
                                                                    }
                                                                />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ) : null
                                                }
                                            />
                                        </FormControl>
                                        <Box
                                            component="h6"
                                            className={classes.dishFormSectionTitle}
                                        >
                                            {i18n.t('common.sizesNBasePrices')}
                                        </Box>
                                        <Box
                                            display="flex"
                                            flexDirection="row"
                                            width="100%"
                                            justifyContent="space-between"
                                        >
                                            <Input
                                                className={clsx(classes.formEdit, {
                                                    [classes.formEditExtended]: showAdditionalForm,
                                                })}
                                                disabled={isFieldDisabled('size')}
                                                variant="filled"
                                                name="size"
                                                type="text"
                                                error={errors.size}
                                                label={i18n.t('common.size')}
                                                placeholder={
                                                    !!values.additionalSizes.length
                                                        ? i18n.t('common.sizeRequired')
                                                        : i18n.t('common.sizeOptional')
                                                }
                                            />
                                            <Input
                                                className={clsx(classes.formEdit, {
                                                    [classes.formEditExtended]: showAdditionalForm,
                                                    [classes.formValueBlurred]:
                                                        menu.type === MenuType.MultiCourse,
                                                })}
                                                disabled={isFieldDisabled(
                                                    'price',
                                                    values.isCustomPrice
                                                )}
                                                variant="filled"
                                                name="price"
                                                type="text"
                                                error={errors.price}
                                                label={i18n.t('form.price')}
                                                placeholder={i18n.t('form.price')}
                                            />
                                        </Box>
                                        <FieldArray
                                            name="additionalSizes"
                                            render={(arrayHelpers) => (
                                                <>
                                                    {values.additionalSizes.map((size, index) => {
                                                        const sizeName = `additionalSizes[${index}].size`;
                                                        const sizeError = getIn(errors, sizeName);
                                                        const priceName = `additionalSizes[${index}].price`;
                                                        const priceError = getIn(errors, priceName);
                                                        return (
                                                            <div
                                                                className={classes.formWrapper}
                                                                key={index}
                                                            >
                                                                <Input
                                                                    className={clsx(
                                                                        classes.formEdit,
                                                                        {
                                                                            [classes.formEditExtended]:
                                                                                showAdditionalForm,
                                                                        }
                                                                    )}
                                                                    disabled={
                                                                        props.disabled ||
                                                                        (isCatalog &&
                                                                            !isCatalogManager)
                                                                    }
                                                                    variant="filled"
                                                                    name={sizeName}
                                                                    type="text"
                                                                    error={sizeError}
                                                                    label={i18n.t('common.size')}
                                                                    placeholder={i18n.t(
                                                                        'common.sizeRequired'
                                                                    )}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <InputAdornment
                                                                                position="start"
                                                                                onClick={() =>
                                                                                    !props.disabled &&
                                                                                    arrayHelpers.remove(
                                                                                        index
                                                                                    )
                                                                                }
                                                                            >
                                                                                <IconButton aria-label="pen">
                                                                                    <RemoveCircleOutlineIcon
                                                                                        fontSize="small"
                                                                                        style={{
                                                                                            color: 'rgba(0, 0, 0, 0.54)',
                                                                                        }}
                                                                                    />
                                                                                </IconButton>
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                />
                                                                <Input
                                                                    className={clsx(
                                                                        classes.formEdit,
                                                                        {
                                                                            [classes.formEditExtended]:
                                                                                showAdditionalForm,
                                                                            [classes.formValueBlurred]:
                                                                                menu.type ===
                                                                                MenuType.MultiCourse,
                                                                        }
                                                                    )}
                                                                    disabled={
                                                                        props.disabled ||
                                                                        values.isCustomPrice ||
                                                                        menu.type ===
                                                                            MenuType.MultiCourse ||
                                                                        (isCatalog &&
                                                                            !isCatalogManager)
                                                                    }
                                                                    variant="filled"
                                                                    name={priceName}
                                                                    type="text"
                                                                    error={priceError}
                                                                    label={i18n.t('form.price')}
                                                                    placeholder={i18n.t(
                                                                        'form.price'
                                                                    )}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                    {((menu.type !== MenuType.MultiCourse &&
                                                        !isCatalog &&
                                                        !props.disabled) ||
                                                        (isCatalog && isCatalogManager)) &&
                                                        !!!props.restaurantName && (
                                                            <Button
                                                                variant="outlined"
                                                                color="primary"
                                                                className={clsx(
                                                                    classes.addMoreSizesButton,
                                                                    {
                                                                        [classes.addMoreSizesButtonExtended]:
                                                                            showAdditionalForm,
                                                                    }
                                                                )}
                                                                style={{
                                                                    width: showAdditionalForm
                                                                        ? title.current
                                                                            ? title.current
                                                                                  .offsetWidth - 34
                                                                            : '100%'
                                                                        : '100%',
                                                                }}
                                                                onClick={() => {
                                                                    arrayHelpers.push({
                                                                        size: '',
                                                                        price: 0,
                                                                        priceLevels:
                                                                            restaurant.pricingLevels.map(
                                                                                (level) => ({
                                                                                    priceLevelId:
                                                                                        level.id,
                                                                                    price: 0,
                                                                                })
                                                                            ),
                                                                    });
                                                                }}
                                                            >
                                                                {`+ ${i18n
                                                                    .t('common.moreSizes')
                                                                    .toUpperCase()}`}
                                                            </Button>
                                                        )}
                                                </>
                                            )}
                                        />
                                        <Box
                                            borderBottom={`1px solid ${theme.palette.divider}`}
                                            marginTop={2}
                                            marginBottom={2}
                                        />
                                        <Box className={classes.formWrapper}>
                                            <Box display="flex" flexDirection="column" width="48%">
                                                <AllergensSelect
                                                    values={values.allergens}
                                                    onChange={(vals: string[]) =>
                                                        setFieldValue('allergens', vals)
                                                    }
                                                    styles={{ marginBottom: 28 }}
                                                    disabled={isFieldDisabled('allergens')}
                                                />
                                            </Box>
                                            <Box display="flex" flexDirection="column" width="48%">
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    className={classes.manageIngredientButton}
                                                    disabled={isFieldDisabled('ingredients')}
                                                    onClick={() =>
                                                        !isFieldDisabled('ingredients') &&
                                                        setShowAddIngredientsModal(true)
                                                    }
                                                >
                                                    {`+ ${i18n
                                                        .t('restaurant.manageIngredients')
                                                        .toUpperCase()}`}
                                                </Button>
                                                <AddIngredientsModal
                                                    defaultIngredients={props.dish.ingredients}
                                                    isVisible={showAddIngredientsModal}
                                                    onClose={() => {
                                                        setShowAddIngredientsModal(false);
                                                    }}
                                                    onSave={(ingredients) => {
                                                        addToAllergens(
                                                            ingredients,
                                                            values,
                                                            setFieldValue
                                                        );
                                                        setFieldValue('ingredients', ingredients);
                                                        setShowAddIngredientsModal(false);
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box display="flex">
                                            <DishDietarySelect<SpicinessLevels>
                                                name={i18n.t('form.spicinessLevel')}
                                                value={values.spicinessLevel}
                                                map={getSpicinessLevels()}
                                                disabled={isFieldDisabled('spicinessLevel')}
                                                onChange={(value: SpicinessLevels) =>
                                                    setFieldValue('spicinessLevel', value)
                                                }
                                            />
                                            <DishDietarySelect<Diets>
                                                name={i18n.t('form.diets')}
                                                value={values.diet}
                                                map={getDiets()}
                                                disabled={isFieldDisabled('diet')}
                                                onChange={(value: Diets) =>
                                                    setFieldValue('diet', value)
                                                }
                                            />
                                            <LabelSelect
                                                selectedLabels={values.labels}
                                                onChange={(labels: string[]) =>
                                                    setFieldValue('labels', labels)
                                                }
                                                disabled={isFieldDisabled('labels')}
                                            />
                                            <DishDietarySelect<DietaryLaws>
                                                name={i18n.t('form.dietaryLaws')}
                                                value={values.dietaryLaw}
                                                map={getDietaryLaws()}
                                                disabled={isFieldDisabled('dietaryLaw')}
                                                onChange={(value: DietaryLaws) =>
                                                    setFieldValue('dietaryLaw', value)
                                                }
                                            />
                                        </Box>
                                    </Box>
                                    <Box
                                        display={
                                            tabActive === DishManagementTabs.ADVANCED
                                                ? 'block'
                                                : 'none'
                                        }
                                    >
                                        {restaurant.coolBoxData.isCoolBox && (
                                            <>
                                                <Box
                                                    component="h6"
                                                    className={classes.dishFormSectionTitle}
                                                >
                                                    {i18n.t('form.supplier')}
                                                </Box>
                                                <Box
                                                    display="flex"
                                                    flexDirection="row"
                                                    width="100%"
                                                    justifyContent="space-between"
                                                >
                                                    <Box
                                                        display="flex"
                                                        flexDirection="column"
                                                        width="48%"
                                                    >
                                                        {restaurant.coolBoxData.isCoolBox && (
                                                            <Input
                                                                disabled={isFieldDisabled(
                                                                    'supplierName'
                                                                )}
                                                                variant="filled"
                                                                fullWidth={true}
                                                                name="supplierName"
                                                                type="text"
                                                                label={i18n.t('form.supplierName')}
                                                                error={errors.supplierName}
                                                                placeholder={i18n.t(
                                                                    'form.supplierName'
                                                                )}
                                                                style={{
                                                                    marginTop: 0,
                                                                    marginBottom: 0,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Box
                                                        display="flex"
                                                        flexDirection="column"
                                                        width="48%"
                                                    >
                                                        {restaurant.coolBoxData.isCoolBox && (
                                                            <Input
                                                                disabled={isFieldDisabled(
                                                                    'externalId'
                                                                )}
                                                                variant="filled"
                                                                fullWidth={true}
                                                                name="externalId"
                                                                type="text"
                                                                label={i18n.t('form.externalId')}
                                                                error={errors.externalId}
                                                                placeholder={i18n.t(
                                                                    'form.externalId'
                                                                )}
                                                                style={{
                                                                    marginTop: 0,
                                                                    marginBottom: 0,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            </>
                                        )}
                                        <Box
                                            component="h6"
                                            className={classes.dishFormSectionTitle}
                                        >
                                            {i18n.t('form.identification')}
                                        </Box>
                                        <Box
                                            display="flex"
                                            flexDirection="row"
                                            width="100%"
                                            justifyContent="space-between"
                                        >
                                            <Box display="flex" flexDirection="column" width="48%">
                                                <div className={classes.flexRowSpaceBetween}>
                                                    <Input
                                                        disabled={isFieldDisabled('barcode')}
                                                        variant="filled"
                                                        fullWidth={true}
                                                        name="barcode"
                                                        type="text"
                                                        label={i18n.t('form.barcode')}
                                                        error={errors.barcode}
                                                        placeholder={i18n.t('form.barcode')}
                                                        style={{
                                                            marginTop: 0,
                                                            marginBottom: 0,
                                                        }}
                                                        InputProps={{
                                                            endAdornment: ((!isCatalog &&
                                                                !props.disabled) ||
                                                                (isCatalog &&
                                                                    isCatalogManager)) && (
                                                                <InputAdornment
                                                                    position="end"
                                                                    onClick={handleScannerOpen}
                                                                >
                                                                    <IconButton>
                                                                        <img
                                                                            className={
                                                                                classes.barcodeIcon
                                                                            }
                                                                            src={
                                                                                images.icons
                                                                                    .barcodeScanIcon
                                                                            }
                                                                            alt="barcode-scanner-icon"
                                                                        />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </div>
                                            </Box>
                                            <Box display="flex" flexDirection="column" width="48%">
                                                <Input
                                                    disabled={isFieldDisabled('primeCost')}
                                                    variant="filled"
                                                    fullWidth={false}
                                                    name="primeCost"
                                                    type="text"
                                                    label={i18n.t('form.primeCost')}
                                                    error={errors.primeCost}
                                                    placeholder={i18n.t('form.primeCost')}
                                                    style={{
                                                        marginTop: 0,
                                                        marginBottom: 0,
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box
                                            component="h6"
                                            className={classes.dishFormSectionTitle}
                                        >
                                            {i18n.t('form.stockManagement')}
                                        </Box>
                                        {!restaurant.coolBoxData.isCoolBox &&
                                            currentPosition === Positions.MANAGER && (
                                                <FormGroup>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                color="primary"
                                                                name="enablePrintingActions"
                                                                checked={
                                                                    values.stockQuantity !== null
                                                                }
                                                                onChange={(e) => {
                                                                    if (
                                                                        values.stockQuantity ===
                                                                        null
                                                                    ) {
                                                                        setFieldValue(
                                                                            'stockQuantity',
                                                                            0
                                                                        );
                                                                    } else {
                                                                        setFieldValue(
                                                                            'stockQuantity',
                                                                            null
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        }
                                                        label={i18n.t('common.trackStockQuantity')}
                                                    />
                                                </FormGroup>
                                            )}
                                        <Typography
                                            variant="body1"
                                            component="p"
                                            className={classes.stockToggleDescription}
                                        >
                                            {values.stockQuantity === null
                                                ? i18n.t('common.itemWillBeAlwaysVisible')
                                                : i18n.t('common.zeroStockWarning')}
                                        </Typography>

                                        {values.stockQuantity !== null && (
                                            <Box
                                                display="flex"
                                                flexDirection="row"
                                                width="100%"
                                                justifyContent="space-between"
                                            >
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    width="48%"
                                                >
                                                    <Input
                                                        disabled={isFieldDisabled('stockQuantity')}
                                                        variant="filled"
                                                        fullWidth={true}
                                                        name="stockQuantity"
                                                        type="text"
                                                        label={i18n.t('form.stockQuantity')}
                                                        error={errors.stockQuantity}
                                                        placeholder={i18n.t('form.stockQuantity')}
                                                    />
                                                </Box>
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    width="48%"
                                                >
                                                    <Input
                                                        disabled={isFieldDisabled('minimumStock')}
                                                        variant="filled"
                                                        fullWidth={true}
                                                        name="minimumStock"
                                                        type="text"
                                                        label={i18n.t('form.minimumStock')}
                                                        error={errors.minimumStock}
                                                        placeholder={i18n.t('form.minimumStock')}
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box
                                        display={
                                            tabActive === DishManagementTabs.PRICE_MANAGEMENT
                                                ? 'block'
                                                : 'none'
                                        }
                                        marginBottom={2}
                                    >
                                        {!!values.priceLevels.length && (
                                            <>
                                                <Box
                                                    component="h6"
                                                    className={classes.dishFormSectionTitle}
                                                >
                                                    {i18n.t('common.setPriceLevels')}
                                                </Box>
                                                <Table className={classes.sizesAndPricesTable}>
                                                    <TableHead>
                                                        <TableRow>
                                                            {values.size && <TableCell></TableCell>}
                                                            {values.priceLevels.map((level) => {
                                                                const label =
                                                                    restaurant.pricingLevels.find(
                                                                        (lev) =>
                                                                            lev.id ===
                                                                            level.priceLevelId
                                                                    )?.name || 'price level';
                                                                return (
                                                                    <TableCell
                                                                        key={level.priceLevelId}
                                                                    >
                                                                        <label>{label}</label>
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        <TableRow>
                                                            {values.size && (
                                                                <TableCell>
                                                                    <label>{values.size}</label>
                                                                </TableCell>
                                                            )}
                                                            {values.priceLevels.map(
                                                                (level, index) => {
                                                                    const label =
                                                                        restaurant.pricingLevels.find(
                                                                            (lev) =>
                                                                                lev.id ===
                                                                                level.priceLevelId
                                                                        )?.name || 'price level';
                                                                    const priceName = `priceLevels[${index}].price`;
                                                                    const priceError = getIn(
                                                                        errors,
                                                                        priceName
                                                                    );
                                                                    return (
                                                                        <TableCell key={index}>
                                                                            <Input
                                                                                disabled={
                                                                                    props.disabled ||
                                                                                    values.isCustomPrice ||
                                                                                    (isCatalog &&
                                                                                        !isCatalogManager)
                                                                                }
                                                                                variant="filled"
                                                                                fullWidth
                                                                                name={priceName}
                                                                                type="text"
                                                                                label={'CHF'}
                                                                                error={priceError}
                                                                                placeholder={'CHF'}
                                                                                style={{
                                                                                    marginBottom: 5,
                                                                                }}
                                                                            />
                                                                        </TableCell>
                                                                    );
                                                                }
                                                            )}
                                                        </TableRow>
                                                        {values.additionalSizes.map(
                                                            (additionalSize, index) => {
                                                                return (
                                                                    <TableRow key={index}>
                                                                        <TableCell>
                                                                            {additionalSize.size}
                                                                        </TableCell>
                                                                        {additionalSize.priceLevels
                                                                            .length
                                                                            ? additionalSize.priceLevels.map(
                                                                                  (
                                                                                      priceLevel,
                                                                                      priceIndex
                                                                                  ) => {
                                                                                      console.log(
                                                                                          additionalSize.priceLevels
                                                                                      );
                                                                                      const label =
                                                                                          restaurant.pricingLevels.find(
                                                                                              (
                                                                                                  level
                                                                                              ) =>
                                                                                                  level.id ===
                                                                                                  priceLevel.priceLevelId
                                                                                          )?.name ||
                                                                                          'additional price';
                                                                                      const priceName = `additionalSizes.[${index}].priceLevels.[${priceIndex}].price`;
                                                                                      const priceError =
                                                                                          getIn(
                                                                                              errors,
                                                                                              priceName
                                                                                          );
                                                                                      return (
                                                                                          <TableCell
                                                                                              key={`${index}-${priceIndex}`}
                                                                                          >
                                                                                              <Input
                                                                                                  fullWidth
                                                                                                  style={{
                                                                                                      marginRight: 5,
                                                                                                      marginBottom: 5,
                                                                                                  }}
                                                                                                  disabled={
                                                                                                      props.disabled ||
                                                                                                      values.isCustomPrice ||
                                                                                                      (isCatalog &&
                                                                                                          !isCatalogManager)
                                                                                                  }
                                                                                                  variant="filled"
                                                                                                  name={
                                                                                                      priceName
                                                                                                  }
                                                                                                  type="text"
                                                                                                  label={
                                                                                                      label
                                                                                                  }
                                                                                                  error={
                                                                                                      priceError
                                                                                                  }
                                                                                                  placeholder={
                                                                                                      label
                                                                                                  }
                                                                                              />
                                                                                          </TableCell>
                                                                                      );
                                                                                  }
                                                                              )
                                                                            : null}
                                                                    </TableRow>
                                                                );
                                                            }
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </>
                                        )}
                                    </Box>
                                    <Box className={classes.bottomPanel}>
                                        <Box
                                            className={classes.bottomContainer}
                                            flexGrow={0.9}
                                            justifyContent="flex-start"
                                        ></Box>
                                        <Box display="flex" flexDirection="row">
                                            {isFieldDisabled('save') ? (
                                                <Button
                                                    type="button"
                                                    color="primary"
                                                    onClick={onCancel}
                                                >
                                                    {i18n.t('button.close')}
                                                </Button>
                                            ) : (
                                                <>
                                                    <Button
                                                        type="button"
                                                        color="primary"
                                                        onClick={onCancel}
                                                        className={clsx(classes.mRight10, {
                                                            [classes.smallFont]: lang === 'de',
                                                        })}
                                                    >
                                                        {i18n.t('button.cancel')}
                                                    </Button>
                                                    <Button
                                                        disabled={formDisabled(values)}
                                                        onClick={submitForm}
                                                        variant="contained"
                                                        color="primary"
                                                    >
                                                        {i18n.t('button.save')}
                                                    </Button>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </>
                                <HandleFormikHelper action={handleShowAdvancedSettingsError} />
                                <SetFieldText field={valueName} text={textToEdit} />
                                <Dialog open={showScanner} onClose={handleScannerClose}>
                                    <DialogContent>
                                        <CodeScanner
                                            onScan={(code: string) => {
                                                setFieldValue('barcode', code);
                                                handleScannerClose();
                                            }}
                                        />
                                    </DialogContent>
                                </Dialog>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
            <TextEditor
                isOpen={showEditor}
                text={textToEdit}
                onClose={() => setShowEditor(false)}
                onSave={saveTextFromEditor}
            />
        </>
    );
}

function HandleFormikHelper({ action }: { action: (hasError: boolean) => void }) {
    const { errors } = useFormikContext();
    const restaurant = useAppSelector((state) => state.restaurantDetails?.restaurantInformation);
    const isCoolBox = restaurant?.coolBoxData?.isCoolBox ?? false;
    useEffect(() => {
        const hasError =
            'barcode' in errors ||
            'primeCost' in errors ||
            'stockQuantity' in errors ||
            'minimumStock' in errors ||
            ('externalId' in errors && isCoolBox);
        action(hasError);
    }, [errors]);
    return null;
}
